/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { classes, Slot } from '@adobe-commerce/elsie/lib';
import { OrdersListCardProps } from '@/account/types';
import { Card, Image, ContentGrid, Price } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/account/components/OrdersListCard/OrdersListCard.css';
import { OrderItem } from '@/account/data/models';
import { useIsMobile } from '@/account/hooks/useIsMobile';
import { checkIsFunction } from '@/account/lib/checkIsFunction';
import { useMemo } from 'preact/hooks';

export const OrdersListCard: FunctionComponent<OrdersListCardProps> = ({
  placeholderImage,
  minifiedView,
  item,
  withThumbnails,
  children,
  slots,
  routeTracking,
  routeOrderProduct,
  routeReturnDetails,
  ...props
}) => {
  const minifiedViewKey = minifiedView ? 'minifiedView' : 'fullSizeView';

  const translations = useText({
    orderNumber: `Account.${minifiedViewKey}.OrdersList.OrdersListCard.orderNumber`,
    itemsAmount: `Account.${minifiedViewKey}.OrdersList.OrdersListCard.itemsAmount`,
    carrier: `Account.${minifiedViewKey}.OrdersList.OrdersListCard.carrier`,
    returnsText: `Account.${minifiedViewKey}.OrdersList.OrdersListCard.returns`,
    deliveryDateText: `Account.${minifiedViewKey}.OrdersList.OrdersListCard.orderDate`,
  });

  const isMobile = useIsMobile();
  const hasProductRoute = checkIsFunction(routeOrderProduct);

  const orderItemsImages = useMemo(() => {
    return (item?.items ?? []).map((orderItem: OrderItem, index) => {
      const defaultImageProps = {
        title: orderItem?.productName ?? '',
        src: orderItem?.product?.smallImage?.url || placeholderImage,
        alt: orderItem?.productName ?? '',
        width: 65,
        height: 65,
        loading: 'eager',
      } as const;

      return (
        <Slot
          key={`${index}_${orderItem.id}`}
          name="OrderItemImage"
          slot={slots?.OrderItemImage}
          context={{
            data: orderItem,
            defaultImageProps,
          }}
        >
          {hasProductRoute ? (
            <a
              href={routeOrderProduct?.(orderItem) ?? ''}
              target="_blank"
              rel="noreferrer"
            >
              <Image {...defaultImageProps} />
            </a>
          ) : (
            <Image {...defaultImageProps} />
          )}
        </Slot>
      );
    });
  }, [
    hasProductRoute,
    item.items,
    placeholderImage,
    routeOrderProduct,
    slots?.OrderItemImage,
  ]);

  return (
    <Card
      variant="secondary"
      {...props}
      className={classes([
        'account-orders-list-card',
        ['account-orders-list-card--full', item?.items?.length > 6],
      ])}
    >
      <div className="account-orders-list-card__content">
        {slots?.OrdersListCard ? (
          <Slot
            data-testid={`ordersListCard${item.id}`}
            name="OrdersListCard"
            slot={slots?.OrdersListCard}
            context={{ orderHistoryListItem: item }}
          />
        ) : (
          <>
            <div>{item?.status}</div>
            <p>
              {translations.deliveryDateText} {item?.orderDate}
            </p>
            <p>
              {translations.orderNumber} {item.number}
            </p>
            {item?.shipments?.map((element) => {
              return element?.tracking?.map((item) => {
                const trackingRoute = routeTracking?.(item);

                return (
                  <p key={element.id} data-testid={item.number}>
                    <span>{`${
                      translations.carrier
                    } ${item.carrier.toLocaleUpperCase()} `}</span>
                    {trackingRoute ? (
                      <a
                        href={trackingRoute}
                        target={'_blank'}
                        className="account-orders-list-card__content--track_number"
                        rel="noreferrer"
                      >
                        {item.number}
                      </a>
                    ) : (
                      <span className="account-orders-list-card__content--track_number">
                        {item.number}
                      </span>
                    )}
                  </p>
                );
              });
            })}
            {item?.returns && item.returns?.length ? (
              <p key={item.id} data-testid={item.number}>
                <span>{translations.returnsText} </span>
                {item?.returns?.map((returnData) => (
                  <span key={returnData.uid} data-testid={returnData.number}>
                    <a
                      href={
                        routeReturnDetails?.({
                          orderNumber: item.number,
                          orderToken: item.token,
                          returnNumber: returnData.number,
                        }) ?? '#'
                      }
                      className="account-orders-list-card__content--return_number"
                    >
                      {returnData.number}{' '}
                    </a>
                  </span>
                ))}
              </p>
            ) : null}
            <p>
              <Price
                amount={item?.total?.grandTotal?.value ?? 0}
                currency={item?.total?.grandTotal?.currency}
                weight="normal"
                size="medium"
              />
            </p>
            <p data-testid={'itemsAmount'}>
              {item?.items?.length
                ? item?.items?.reduce((sum, element) => {
                    const quantityOrdered = element?.quantityOrdered || 0;
                    return sum + quantityOrdered;
                  }, 0)
                : 0}{' '}
              {translations.itemsAmount}
            </p>
            {item?.items?.map((element, index) => {
              if (index >= 0 && index < 10) {
                return (
                  <p
                    key={`${index}_${element.id}`}
                    className={
                      'account-orders-list-card__content--product-name'
                    }
                  >
                    <span className="account-orders-list-card__content--quantity">
                      {element?.quantityOrdered! > 1
                        ? element?.quantityOrdered
                        : null}{' '}
                    </span>
                    {element?.productName.replaceAll('-', ' ')}
                  </p>
                );
              }

              if (index === 11) return <p key={'ellipsis'}>...</p>;
            })}
          </>
        )}
      </div>
      {withThumbnails && item?.items?.length ? (
        <ContentGrid
          maxColumns={isMobile ? 3 : 9}
          emptyGridContent={<></>}
          className={classes([
            'account-orders-list-card__images',
            ['account-orders-list-card__images-3', isMobile],
          ])}
          data-testid="ordersListCardImages"
        >
          {orderItemsImages}
        </ContentGrid>
      ) : null}

      <div className="account-orders-list-card__actions">{children}</div>
    </Card>
  );
};
